import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Separator } from "@/components/ui/separator";

interface SessionDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
}

export default function SessionDetailsModal({
  open,
  onOpenChange,
  sessionId,
}: SessionDetailsModalProps) {
  const { data: sessionDetails, isLoading } = useQuery({
    queryKey: ["session-details", sessionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cash_register_sessions")
        .select(`
          *,
          bank_accounts (id, nome, icone),
          profiles_opened:profiles!cash_register_sessions_opened_by_fkey (nome_completo),
          profiles_closed:profiles!cash_register_sessions_closed_by_fkey (nome_completo),
          cash_register_breakdown (
            *,
            bank_accounts (nome),
            payment_methods:payment_method_id (id, nome, slug)
          ),
          transactions (
            id, tipo, valor_liquido, descricao, created_at,
            clients (nome),
            suppliers (nome),
            payment_methods (nome)
          )
        `)
        .eq("id", sessionId)
        .single();

      if (error) throw error;

      // Buscar pagamentos para calcular entradas usando net_amount
      const openingDate = data.opened_at;
      const closingDate = data.closed_at || new Date().toISOString();

      // Pagamentos de transações vinculadas à sessão
      const { data: paysBySession } = await supabase
        .from('transaction_payments')
        .select('id, transaction_id, status, valor, net_amount, data_prevista, payment_method_id, transactions!inner(id, created_at, cash_register_session_id)')
        .eq('transactions.cash_register_session_id', sessionId)
        .gte('transactions.created_at', openingDate)
        .lte('transactions.created_at', closingDate);

      // Pagamentos de transações sem sessão no período
      const { data: paysNoSession } = await supabase
        .from('transaction_payments')
        .select('id, transaction_id, status, valor, net_amount, data_prevista, payment_method_id, transactions!inner(id, created_at, tenant_id)')
        .eq('transactions.tenant_id', data.tenant_id)
        .is('transactions.cash_register_session_id', null)
        .gte('transactions.created_at', openingDate)
        .lte('transactions.created_at', closingDate);

      const allPayments = [...(paysBySession || []), ...(paysNoSession || [])];
      const closingDateObj = new Date(closingDate);

      // Calcular total de entradas usando net_amount dos pagamentos confirmados e liquidados
      const totalEntradasFromPayments = allPayments
        .filter((p: any) => {
          const status = (p.status || 'pendente') as string;
          if (status !== 'confirmado') return false;
          // Verificar se já liquidou (data_prevista <= data de fechamento)
          const dataPrevista = p.data_prevista ? new Date(p.data_prevista) : null;
          const isLiquidated = !dataPrevista || dataPrevista <= closingDateObj;
          return isLiquidated;
        })
        .reduce((sum: number, p: any) => {
          // Usar net_amount (valor líquido após taxa) em vez de valor bruto
          const netValue = typeof p.net_amount === "number" ? Number(p.net_amount) : Number(p.valor) || 0;
          return sum + netValue;
        }, 0);

      // Buscar métodos de pagamento para enriquecer as transações
      const pmIds = Array.from(new Set((allPayments || []).map((p: any) => p.payment_method_id).filter(Boolean)));
      const { data: pms } = pmIds.length
        ? await supabase.from('payment_methods').select('id, nome').in('id', pmIds)
        : { data: [] as any[] } as any;
      const pmNameById = new Map<string, string>();
      (pms || []).forEach((m: any) => pmNameById.set(m.id, m.nome));

      // Agrupar pagamentos por transaction_id e calcular valor líquido total por transação
      const paymentsByTxId = new Map<string, any[]>();
      allPayments.forEach((p: any) => {
        // Usar transaction_id direto ou do relacionamento
        const txId = p.transaction_id || p.transactions?.id;
        if (txId) {
          if (!paymentsByTxId.has(txId)) {
            paymentsByTxId.set(txId, []);
          }
          paymentsByTxId.get(txId)!.push(p);
        }
      });

      // Enriquecer transações com valor líquido e método de pagamento
      const enrichedTransactions = (data.transactions || []).map((tx: any) => {
        if (tx.tipo === 'entrada') {
          const payments = paymentsByTxId.get(tx.id) || [];
          // Calcular valor líquido total de TODOS os pagamentos confirmados (não apenas liquidados)
          // Isso mostra o valor real da transação, independente de quando vai liquidar
          const totalLiquidValue = payments
            .filter((p: any) => {
              const status = (p.status || 'pendente') as string;
              return status === 'confirmado';
            })
            .reduce((sum: number, p: any) => {
              // Usar net_amount (valor líquido após taxa) quando disponível
              const netValue = typeof p.net_amount === "number" ? Number(p.net_amount) : Number(p.valor) || 0;
              return sum + netValue;
            }, 0);
          
          // Pegar métodos de pagamento únicos (ou concatenar se houver múltiplos)
          const paymentMethods = Array.from(new Set(
            payments
              .filter((p: any) => p.payment_method_id)
              .map((p: any) => pmNameById.get(p.payment_method_id) || null)
              .filter(Boolean)
          ));
          const methodName = paymentMethods.length > 0 
            ? (paymentMethods.length > 1 ? paymentMethods.join(', ') : paymentMethods[0])
            : '-';

          return {
            ...tx,
            // Usar valor líquido calculado dos pagamentos, ou fallback para valor_liquido da transação
            valor_liquido: totalLiquidValue > 0 ? totalLiquidValue : tx.valor_liquido,
            payment_method_name: methodName,
          };
        }
        return tx;
      });

      return {
        ...data,
        totalEntradasFromPayments,
        enrichedTransactions,
      };
    },
    enabled: !!sessionId && open,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle>Detalhes do fechamento de Caixas</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center text-muted-foreground">
            Carregando...
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!sessionDetails) return null;

  // Filtrar transações apenas dentro do período da sessão
  const openingDate = new Date(sessionDetails.opened_at);
  const closingDate = sessionDetails.closed_at ? new Date(sessionDetails.closed_at) : new Date();
  
  // Usar transações enriquecidas se disponíveis, senão usar as originais
  const transactionsToUse = sessionDetails.enrichedTransactions || sessionDetails.transactions || [];
  
  const filteredTransactions = transactionsToUse.filter((tx: any) => {
    const txDate = new Date(tx.created_at);
    return txDate >= openingDate && txDate <= closingDate;
  });

  // Usar total de entradas calculado a partir dos pagamentos (net_amount)
  const totalEntradas = sessionDetails.totalEntradasFromPayments || 0;

  const totalSaidas = filteredTransactions
    .filter((t) => t.tipo === "saida")
    .reduce((sum, t) => sum + t.valor_liquido, 0) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col min-h-0 overflow-hidden p-0">
        <DialogHeader className="px-6 py-4 border-b bg-background sticky top-0 z-10">
          <DialogTitle>Detalhes do fechamento de Caixas</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-6">
          {/* Informações Gerais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Informações Gerais ({sessionDetails.status=='closed' ? 'Fechada' : 'Aberta'})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Aberto por</p>
                  <p className="font-medium">{sessionDetails.profiles_opened?.nome_completo}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data/Hora Abertura</p>
                  <p className="font-medium">
                    {format(new Date(sessionDetails.opened_at), "dd/MM/yyyy 'às' HH:mm", {
                      locale: ptBR,
                    })}
                  </p>
                </div>
                {sessionDetails.closed_at && (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Fechado por</p>
                      <p className="font-medium">
                        {sessionDetails.profiles_closed?.nome_completo}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Data/Hora Fechamento</p>
                      <p className="font-medium">
                        {format(new Date(sessionDetails.closed_at), "dd/MM/yyyy 'às' HH:mm", {
                          locale: ptBR,
                        })}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {sessionDetails.opening_notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground">Observações de Abertura</p>
                    <p className="text-sm mt-1">{sessionDetails.opening_notes}</p>
                  </div>
                </>
              )}

              {sessionDetails.closing_notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground">Observações de Fechamento</p>
                    <p className="text-sm mt-1">{sessionDetails.closing_notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Resumo Financeiro */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Resumo Financeiro</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Saldo Inicial</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(sessionDetails.opening_balance)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Entradas</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(totalEntradas)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Saídas</p>
                  <p className="text-lg font-semibold text-red-600">
                    {formatCurrency(totalSaidas)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Saldo Esperado</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(sessionDetails.expected_balance || 0)}
                  </p>
                </div>
              </div>

              {sessionDetails.status === "closed" && (
                <>
                  <Separator className="my-4" />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Saldo Declarado</p>
                      <p className="text-lg font-semibold">
                        {formatCurrency(sessionDetails.declared_balance || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Diferença</p>
                      <p
                        className={`text-lg font-bold ${
                          Math.abs(sessionDetails.difference || 0) > 0.01
                            ? "text-destructive"
                            : "text-muted-foreground"
                        }`}
                      >
                        {formatCurrency(sessionDetails.difference || 0)}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Breakdown por Método */}
          {(() => {
            // Filtrar apenas movimentos (phase='movement') que têm método de pagamento
            const movementBreakdowns = (sessionDetails.cash_register_breakdown || []).filter(
              (b: any) => b.phase === 'movement' && b.payment_method_id != null
            );
            
            return movementBreakdowns.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Somatórias por Conta  e Método de Pagamento</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Conta</TableHead>
                        <TableHead>Método</TableHead>
                        <TableHead className="text-right">Esperado</TableHead>
                        <TableHead className="text-right">Declarado</TableHead>
                        <TableHead className="text-right">Diferença</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movementBreakdowns.map((breakdown: any) => {
                        const hasDifference = Math.abs(breakdown.difference || 0) > 0.01;

                        return (
                          <TableRow key={breakdown.id}>
                            <TableCell>{breakdown.bank_accounts?.nome}</TableCell>
                            <TableCell>{breakdown.payment_methods?.nome || '-'}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(breakdown.expected_amount)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(breakdown.declared_amount)}
                            </TableCell>
                            <TableCell
                              className={`text-right font-medium ${
                                hasDifference ? "text-destructive" : "text-muted-foreground"
                              }`}
                            >
                              {formatCurrency(breakdown.difference || 0)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : null;
          })()}

          {/* Transações */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Transações ({filteredTransactions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredTransactions.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nenhuma transação no período desta sessão
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Cliente/Fornecedor</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTransactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell>
                          {format(new Date(tx.created_at), "dd/MM HH:mm", { locale: ptBR })}
                        </TableCell>
                        <TableCell className="capitalize">
                          <span
                            className={
                              tx.tipo === "entrada" ? "text-green-600" : "text-red-600"
                            }
                          >
                            {tx.tipo}
                          </span>
                        </TableCell>
                        <TableCell>
                          {tx.clients?.nome || tx.suppliers?.nome || "-"}
                        </TableCell>
                        <TableCell>{tx.payment_method_name || tx.payment_methods?.nome || "-"}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {tx.descricao || "-"}
                        </TableCell>
                        <TableCell
                          className={`text-right font-medium ${
                            tx.tipo === "entrada" ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {formatCurrency(tx.valor_liquido)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

        </div>

        <div className="bg-background px-6 py-4 border-t flex justify-end sticky bottom-0 z-10">
          <Button onClick={() => onOpenChange(false)}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
